import React, { useState } from "react";

function AutocompleteField({
    id,
    label,
    placeholder,
    value,
    options,
    onChange,
    disabled = false,
    inputClass,
    wrapperClass = "space-y-2",
}) {
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const normalizedValue = value.trim().toLowerCase();
    const suggestions = options
        .filter((option) => option.toLowerCase().includes(normalizedValue));

    const selectSuggestion = (option) => {
        onChange(option);
        setIsSuggestionsOpen(false);
        setHighlightedIndex(-1);
    };

    return (
        <div className={wrapperClass}>
            <label htmlFor={id} className="text-sm font-semibold text-slate-200 px-1">{label}</label>
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    className={`${inputClass} pr-10 ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    onFocus={() => {
                        if (disabled) return;
                        setIsSuggestionsOpen(true);
                        setHighlightedIndex(-1);
                    }}
                    onBlur={() => {
                        setTimeout(() => {
                            setIsSuggestionsOpen(false);
                            setHighlightedIndex(-1);
                        }, 120);
                    }}
                    onChange={(event) => {
                        if (disabled) return;
                        onChange(event.target.value);
                        setIsSuggestionsOpen(true);
                        setHighlightedIndex(-1);
                    }}
                    onKeyDown={(event) => {
                        if (disabled) return;

                        if (event.key === "Escape") {
                            setIsSuggestionsOpen(false);
                            setHighlightedIndex(-1);
                        }

                        if (event.key === "ArrowDown") {
                            event.preventDefault();

                            if (!isSuggestionsOpen) {
                                setIsSuggestionsOpen(true);
                                setHighlightedIndex(0);
                                return;
                            }

                            setHighlightedIndex((currentIndex) => {
                                if (suggestions.length === 0) return -1;
                                const nextIndex = currentIndex + 1;
                                return nextIndex >= suggestions.length ? 0 : nextIndex;
                            });
                        }

                        if (event.key === "ArrowUp") {
                            event.preventDefault();

                            if (!isSuggestionsOpen) {
                                setIsSuggestionsOpen(true);
                                setHighlightedIndex(suggestions.length > 0 ? suggestions.length - 1 : -1);
                                return;
                            }

                            setHighlightedIndex((currentIndex) => {
                                if (suggestions.length === 0) return -1;
                                if (currentIndex <= 0) return suggestions.length - 1;
                                return currentIndex - 1;
                            });
                        }

                        if (event.key === "Enter" && isSuggestionsOpen && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                            event.preventDefault();
                            selectSuggestion(suggestions[highlightedIndex]);
                        }
                    }}
                />

                {value.trim() !== "" && (
                    <button
                        type="button"
                        aria-label={`Limpiar ${label}`}
                        disabled={disabled}
                        className="absolute inset-y-0 right-8 flex items-center text-slate-300 hover:text-white"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            if (disabled) return;
                            onChange("");
                            setIsSuggestionsOpen(false);
                            setHighlightedIndex(-1);
                        }}
                    >
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
                            <path d="M6 6L14 14" />
                            <path d="M14 6L6 14" />
                        </svg>
                    </button>
                )}

                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true"><path d="m5 7 5 5 5-5" /></svg>
                </span>

                {isSuggestionsOpen && suggestions.length > 0 && (
                    <ul
                        className="absolute z-30 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-white/20 bg-[#0c3a24] shadow-xl"
                        role="listbox"
                        aria-label={`Sugerencias para ${label}`}
                    >
                        {suggestions.map((option, index) => (
                            <li key={option}>
                                <button
                                    type="button"
                                    className={`w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 ${highlightedIndex === index ? "bg-white/10" : ""}`}
                                    onMouseDown={() => {
                                        selectSuggestion(option);
                                    }}
                                >
                                    {option}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function BuscarPartidoFilters({
    isLoading,
    ligas,
    etapas,
    canchas,
    adversarios,
    localias,
    resultados,
    alargues,
    torneo,
    etapa,
    cancha,
    adversario,
    localia,
    resultado,
    alargue,
    onTorneoChange,
    onEtapaChange,
    onCanchaChange,
    onAdversarioChange,
    onLocaliaChange,
    onResultadoChange,
    onAlargueChange,
    onBuscar,
    onLimpiar,
}) {
    const [isOpen, setIsOpen] = useState(true);

    const activeCount = [torneo, etapa, cancha, adversario, localia, resultado, alargue].filter(Boolean).length;

    const inputClass = "w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500";

    return (
        <>
            <div className="flex items-center justify-between px-1 md:block">
                <div className="flex flex-col gap-1">
                    <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">Filtros</h2>
                    <p className="text-slate-400 text-xs sm:text-sm">Refiná tu búsqueda de partidos</p>
                </div>
                <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => setIsOpen(!isOpen)}
                    className={`md:hidden flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/20 text-sm text-slate-300 ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                    {isOpen ? "Ocultar" : "Mostrar"}
                    {activeCount > 0 && (
                        <span className="bg-green-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {activeCount}
                        </span>
                    )}
                    <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    >
                        <path d="m5 7 5 5 5-5" />
                    </svg>
                </button>
            </div>

            <div className={`${isOpen ? "block" : "hidden"} md:block`}>
                <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-xl p-4 flex flex-col gap-4">
                    {isLoading && (
                        <div className="rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-xs sm:text-sm text-slate-200">
                            Cargando listados de filtros, esperá un momento...
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-3 md:gap-4">
                        <AutocompleteField
                            id="filtro-torneo"
                            label="Temporada"
                            placeholder="Todas"
                            value={torneo}
                            options={ligas}
                            onChange={onTorneoChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                            wrapperClass="space-y-2 sm:col-span-2 md:col-span-1"
                        />

                        <AutocompleteField
                            id="filtro-etapa"
                            label="Etapa"
                            placeholder="Todas"
                            value={etapa}
                            options={etapas}
                            onChange={onEtapaChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                        />

                        <AutocompleteField
                            id="filtro-cancha"
                            label="Cancha"
                            placeholder="Todas"
                            value={cancha}
                            options={canchas}
                            onChange={onCanchaChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                        />

                        <AutocompleteField
                            id="filtro-adversario"
                            label="Rival"
                            placeholder="Todos"
                            value={adversario}
                            options={adversarios}
                            onChange={onAdversarioChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                            wrapperClass="space-y-2 sm:col-span-2 md:col-span-1"
                        />

                        <AutocompleteField
                            id="filtro-localia"
                            label="Condición"
                            placeholder="Todas"
                            value={localia}
                            options={localias}
                            onChange={onLocaliaChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                        />

                        <AutocompleteField
                            id="filtro-resultado"
                            label="Resultado"
                            placeholder="Todos"
                            value={resultado}
                            options={resultados}
                            onChange={onResultadoChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                        />

                        <AutocompleteField
                            id="filtro-alargue"
                            label="Alargue"
                            placeholder="Todos"
                            value={alargue}
                            options={alargues}
                            onChange={onAlargueChange}
                            disabled={isLoading}
                            inputClass={inputClass}
                        />
                    </div>

                    <div className="pt-2 flex flex-col gap-2">
                        <button
                            disabled={isLoading}
                            className={`w-full py-3 bg-green-500 text-black font-bold rounded-lg shadow-lg shadow-green-900/20 transition-opacity text-sm ${isLoading ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"}`}
                            onClick={() => { onBuscar(); setIsOpen(false); }}
                        >
                            Aplicar filtros
                        </button>
                        <button
                            disabled={isLoading}
                            className={`w-full py-2 text-sm font-medium transition-colors ${isLoading ? "text-red-400/60 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                            onClick={onLimpiar}
                        >
                            Limpiar filtros
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BuscarPartidoFilters;
